import {computed, inject, Injectable, Injector, signal, Signal} from "@angular/core";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {firstValueFrom, from, map, of, switchMap} from "rxjs";
import {Router} from "@angular/router";
import {INTRO_ELIGIBILITY_STATUS, Purchases} from "@revenuecat/purchases-capacitor";
import {isNative, platform, waitAsync} from "../../utils";
import {environment} from "../../../../environments/environment";
import {DialogService} from "primeng/dynamicdialog";
import {NotPaidDialogComponent} from "./not-paid-dialog/not-paid-dialog.component";
import {
  CreateCheckoutSessionCommand,
  CreateSubscriptionCommand,
  CreateSubscriptionResponse
} from "homey-housing-lottery-app-dto";
import {ABService} from "../a-b.service";
import {AnalyticsService} from "../analytics.service";
import {assert} from "typia";
import {UserService} from "../user.service";
import {ExternalCodeService} from "../external-code.service";
import {Browser} from '@capacitor/browser';
import {App} from "@capacitor/app";
import {AppLauncher} from "@capacitor/app-launcher";


import {ResubscribeDialogComponent} from "../../components/resubscribe-dialog/resubscribe-dialog.component";
import {StorageService} from "../storage.service";
import {isPostSignupFlowUser} from "../../model/user";
import {DrawerService} from "../drawer.service";
import {BenefitsPageStyleOverride, DebugService} from "../debug.service";
import {NotificationService} from "../notification/notification.service";
import {
  AutoApplyStartContentComponent
} from "../notification/notification-dialog-content/auto-apply-start-lotteries-dialog-content/auto-apply-start-content.component";

declare let window: any;

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly storageService = inject(StorageService)
  private readonly abService = inject(ABService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly injector = inject(Injector);
  private readonly externalCodeService = inject(ExternalCodeService);
  private readonly userService = inject(UserService);
  private readonly drawerService = inject(DrawerService);
  private readonly notificationService = inject(NotificationService);
  private readonly debugService = inject(DebugService);
  private readonly monthlyPriceOption = this.abService.priceOption;

  public readonly appFreeTrialUnavailable = signal(false);

  public readonly benefitsPageStyle = computed<NonNullable<BenefitsPageStyleOverride>>(() => {
    const override = this.debugService.benefitsPageStyleOverride();
    if (override) {
      return override;
    }

    const user = this.userService.user();
    const isDeferred = !!user && user.status === 'mandatory-flow' && user.paymentInfo.type === 'deferred';
    const hasTrial = isDeferred ? user.paymentInfo.includeTrial : true;

    if (isNative) {
      if (!hasTrial || this.appFreeTrialUnavailable()) {
        return "native-no-trial";
      }
      return isDeferred ? "native-sunk" : "native-initial";
    }

    if (!isDeferred) {
      return "initial";
    }

    return hasTrial ? "deferred-with-trial" : "deferred-without-trial";
  });

  async checkTrialAvailability() {
    if (platform !== 'ios') {
      return;
    }

    try {
      const aPackage = (await Purchases.getOfferings()).current?.monthly;
      if (!aPackage) {
        return;
      }

      const statuses = await Purchases.checkTrialOrIntroductoryPriceEligibility({
        productIdentifiers: [aPackage.product.identifier]
      });

      const eligibility = statuses[aPackage.product.identifier];
      if (eligibility.status !== INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_ELIGIBLE) {
        this.appFreeTrialUnavailable.set(true);
      }
    } catch {
      // Silently fail - default to trial available
    }
  }

  readonly freeTrialDuration = computed(
    () => {
      switch (this.abService.freeTrialOption()) {
        case "14-days":
          return 14;
        case "7-days":
          return 7;
      }
    }
  );

  public getPrice(discounted: Signal<boolean>): Signal<number> {
    return computed(() => {
      if (discounted()) {
        return this.discountedMonthlyPriceValue();
      } else {
        return this.monthlyPriceValue();
      }
    });
  }

  public showNotPaidPopup() {
    this.dialogService.open(NotPaidDialogComponent, {
      header: 'Subscription inactive',
      closable: true,
      modal: true,
      focusOnShow: false,
      width: 'calc(min(100vw - 10px, 500px))',
    });
  }

  public async openBenefitsDrawer(
    inputs?: {payment?: string; disableRouteSync?: boolean},
  ): Promise<void> {
    const {BenefitsPageComponent} = await import('../../pages/signup/benefits-page/benefits-page.component');
    const drawerInputs: Record<string, unknown> = {};

    if (inputs?.payment !== undefined) {
      drawerInputs['payment'] = inputs.payment;
    }
    if (inputs?.disableRouteSync !== undefined) {
      drawerInputs['disableRouteSync'] = inputs.disableRouteSync;
    }

    const style = this.benefitsPageStyle();
    const supportsTrial = style !== 'deferred-without-trial';
    const header = supportsTrial ? `` : `<p class="mt-8 text-2xl text-orange-gradient">Subscribe to continue</p>`;
    const styleClass = 'homey-drawer-bottom';

    this.drawerService.open(BenefitsPageComponent, {
      inputs: drawerInputs,
      styleClass,
      header: {
        content: header,
        style: supportsTrial ? 'left' : 'centered',
      },
      size: 'auto',
    });
  }

  private readonly getCommand = (paymentMethodId: string, discount: boolean): CreateSubscriptionCommand => {
    return {
      withCode: this.externalCodeService.savedCode(),
      billingMode: this.getMontlyBillingMode(discount), paymentMethodId,
      freeTrialDuration: this.abService.freeTrialOption()
    }
  }

  private readonly getCreateSubscriptionCommand = (
                                                   checkoutFlow: 'initial' | 'deferred' | 'deferred-no-trial' | 'resubscribe',
                                                   freeTrialMode: "regular" | "no-trial", discount: boolean) => {
    return {
      abGroup: this.abService.stripeABGroup(),
      billingMode: this.getMontlyBillingMode(discount),
      withCode: this.externalCodeService.savedCode(),
      freeTrialDuration: freeTrialMode === "regular" ? this.abService.freeTrialOption() : 'no-trial',
      checkoutFlow,
    }
  }

  public readonly monthlyPriceType = this.abService.priceOption;

  public readonly monthlyPriceValue = computed(() => {
    switch (this.monthlyPriceType()) {
      case "15-monthly":
        return 14.99;
      case "regular-monthly":
        return 13.31;
      case "promotion-monthly":
        return 9.99;
      case "10-monthly":
        return 9.99;
      case "20-monthly":
        return 19.99;
    }
  });

  public readonly canBacktrackDiscount = computed(() => {
    return this.monthlyPriceType() === "15-monthly" || this.monthlyPriceType() === "20-monthly";
  })

  public readonly discountedMonthlyPriceValue = computed(() => {
    return Math.round((this.monthlyPriceValue() - 5 + Number.EPSILON) * 100) / 100
  })

  private readonly getMontlyBillingMode = (discount: boolean) => {
    switch (this.monthlyPriceOption()) {
      case "regular-monthly":
        return "monthly";
      case "promotion-monthly":
        return "monthly-promotion";
      case "15-monthly":
        if (discount) {
          return "monthly-10-backtrack";
        } else {
          return "monthly-higher";
        }
      case "10-monthly":
        return "monthly-10";
      case "20-monthly":
        if (discount) {
          return "monthly-15-backtrack";
        } else {
          return "monthly-20";
        }
    }
  }

  public readonly startPayment = (
                                  discount: boolean,
                                  mode: 'initial' | 'resubscribe' | 'deferred'
  ) => {
    const flow = mode === 'deferred' ? 'initial' : mode;
    const checkoutLocation = mode === 'deferred'
      ? (this.isDeferredNoTrial() ? 'deferred-no-trial' : 'deferred')
      : 'default';
    const checkoutFlow = mode === 'deferred' && this.isDeferredNoTrial() ? 'deferred-no-trial' : mode;

    void this.analyticsService.sendPaymentStarted({
      flow,
      interval: 'monthly',
      checkoutLocation,
      amount: 15,
    });

    const useStripeCheckout = platform === 'web' || (platform === 'ios' && this.abService.nativePayment() === 'stripe');

    if (!useStripeCheckout) {
      return from(this.startPurchaseCatPayment(mode));
    }

    const freeTrialMode = (() => {
      if (mode === 'deferred' && this.isDeferredNoTrial()) {
        return 'no-trial';
      }

      return mode === 'initial' || mode === 'deferred' ? 'regular' : 'no-trial';
    })();

    return this.http.post<unknown>(`${environment.apiUrl}/api/private/payment/create-checkout-session`, this.getCreateSubscriptionCommand(
      checkoutFlow,
      freeTrialMode,
      discount
    )).pipe(
      switchMap((response) => {
        const url = assert<{ url: string }>(response).url;
        if (isNative) {
          return from(AppLauncher.openUrl({url}).then(() => {}));
        }
        window.location.href = url;
        return of();
      })
    );
  }

  public openFixPaymentPage = async () => {
    this.analyticsService.sendEvent("fix-past-due-started")
    return this.createCustomerPortalSession("past-due")
  }

  public async startPurchaseCatPayment(
    mode: 'initial' | 'resubscribe' | 'deferred',
  ): Promise<void> {
    const aPackage = (await Purchases.getOfferings()).current?.monthly;
    if (!aPackage) {
      throw new Error('No monthly package found');
    }

    try {
      await Purchases.purchasePackage({aPackage});
      const targetRoute = mode === 'deferred'
        ? ['/payment', 'deferred-success']
        : mode === 'resubscribe'
          ? ['/payment', 'resubscribe-success']
          : ['/payment', 'success'];
      await this.router.navigate(targetRoute);
    } catch (error) {
      console.error('Revenuecat purchase failed:', error);
    }
  }

  public readonly runAutoApplyAfterResubscribe = async () => {
    try {
      const user = this.userService.user();

      if (!user || user.status !== 'active' || !user.autoApplySettings.enabled) {
        return;
      }

      const lotteries = await firstValueFrom(this.userService.refreshAutoApply());

      if (lotteries.length === 0) {
        return;
      }

      this.notificationService.openNotificationDialog(
        [
          {
            component: AutoApplyStartContentComponent,
            data: {lotteries},
          }
        ]
      );
    } catch (error) {
      console.error('Failed to refresh auto-apply after resubscribe:', error);
    }
  };

  public createSetupIntent() {
    return this.http.post<unknown>(`${environment.apiUrl}/api/private/payment/create-setup-intent`, {
    }).pipe(
      map(response => assert<{ clientSecret: string }>(response).clientSecret)
    );
  }

  public async initialize() {
    if (isNative) {
      await this.configureRevenueCat();
    }

    void this.handlePastDue();
  }

  private async handlePastDue() {
    const pastDueStarted = await this.storageService.getItem('PAST_DUE_FLOW_STARTED');
    if (pastDueStarted) {
      await this.storageService.removeItem('PAST_DUE_FLOW_STARTED');
      await this.startReturnFromPastDueProcedure();
    }
  }

  private async configureRevenueCat() {
    this.injector.get(UserService).user$.subscribe(
      user => {
        if (user.status === 'active' || user.status === 'mandatory-flow' || user.status === 'signup-flow') {
          Purchases.logIn({
            appUserID: user.id
          })
        }
      }
    );

    const platformValue = platform;

    let apiKey: string;

    if (platformValue === 'ios') {
      apiKey = 'appl_LYRiqkGMctcPidZTLnKGBqOXgYz'; // Replace with your actual RevenueCat API key for iOS from the dashboard.
    } else if (platformValue === 'android') {
      apiKey = 'goog_CCtnYcFmiqKExHurYVBHrgguQDC'; // Replace with your actual RevenueCat API key for Android from the dashboard.
    } else {
      throw new Error('Unsupported platform for RevenueCat configuration');
    }

    await Purchases.configure({
      apiKey: apiKey, // Replace with your actual RevenueCat API key from the dashboard.
      appUserID: null,                  // Set to null to let RevenueCat generate a user ID automatically, or provide your own.
      diagnosticsEnabled: true,         // Optional, defaults to false
    });
  }

  public createCustomerPortalSession = async (origin: 'details' | 'past-due') => {
    const user = this.userService.user();
    const paymentInfo = user && user.status !== 'unauthorized' && user.status !== 'signup-flow' ? user.paymentInfo : undefined;
    if (paymentInfo && paymentInfo.type === 'revenuecat') {
      switch (platform) {
        case 'ios':
          await Browser.open({url: 'https://apps.apple.com/account/subscriptions'});
          break;
        case 'android':
          { const {id: packageName} = await App.getInfo();
          const url = `https://play.google.com/store/account/subscriptions?package=${packageName}`;
          await Browser.open({url});
          break; }
        default:
          switch(paymentInfo.os) {
            case 'ios':
              await Browser.open({url: 'https://apps.apple.com/account/subscriptions'});
              break;
            case 'android':
              { const playUrl = `https://play.google.com/store/account/subscriptions`;
              await Browser.open({url: playUrl});
              break; }
          }
      }
    } else {
      if (origin === 'past-due') {
        await this.storageService.setItem('PAST_DUE_FLOW_STARTED', true)
      }

      if (isNative) {
        // Native: no popup blocker issues
        const response = await firstValueFrom(
          this.http.post(`${environment.apiUrl}/api/private/payment/create-customer-portal-session`, {}, {
            responseType: 'text',
            params: { origin }
          })
        );
        await AppLauncher.openUrl({ url: response });
      } else {
        // Web: pre-open tab to avoid popup blocker
        const windowRef = window.open('about:blank', '_blank');
        const response = await firstValueFrom(
          this.http.post(`${environment.apiUrl}/api/private/payment/create-customer-portal-session`, {}, {
            responseType: 'text',
            params: { origin }
          })
        );
        if (windowRef) {
          windowRef.location.href = response;
        } else {
          window.location.href = response;
        }
      }
    }
  }

  public retryFailedPayment = () => {
    return this.http.post<unknown>(`${environment.apiUrl}/api/private/payment/retry-failed-payment`, {}).pipe();
  }

  public extendSubscription = () => {
    return this.http.post<unknown>(`${environment.apiUrl}/api/private/payment/extend-subscription`, {}).pipe(
      switchMap(
        () => this.injector.get(UserService).retrieveUser()
      )
    )
  }

  public discountStayOnHomeySubscription = () => {
    return this.http.post<unknown>(`${environment.apiUrl}/api/private/payment/subscription-discount`, {
      type: 'stay-on-homey-50'
    }).pipe(
      switchMap(
        () => this.injector.get(UserService).retrieveUser()
      )
    )
  }

  public extendTrial = () => {
    return this.http.post<unknown>(`${environment.apiUrl}/api/private/payment/extend-trial`, {}).pipe(
      switchMap(
        () => this.injector.get(UserService).retrieveUser()
      )
    )
  }

  public openResubscribeDialog = () => {
    this.analyticsService.sendEvent('resubscribe-flow-started');

    const variant = this.abService.resubscribeFlow();

    if (variant === 'direct') {
      this.startPayment(false, 'resubscribe').subscribe();
      return;
    }

    this.dialogService.open(
      ResubscribeDialogComponent,
      {
        header: 'Re-subscribe',
        closable: true,
        modal: true,
        focusOnShow: false,
        width: 'calc(min(100vw - 10px, 500px))',
      }
    );
  }

  public createSubscription = (paymentMethodId: string) => {
    return this.http.post<unknown>(`${environment.apiUrl}/api/private/payment/create-subscription`, this.getCommand(
      paymentMethodId,
      false
    )).pipe(
      map(res => assert<CreateSubscriptionResponse>(res))
    );
  }

  public readonly pastDueRecoveryStep = signal<'not-started' | 'waiting' | 'retrying' | 'success' | 'retry-failed'>('not-started')
  private readonly pastDueRetryFailureReason = signal<'none' | 'insufficient-funds'>('none');

  public readonly pastDueDialogHeader = computed(
    () => {
      switch (this.pastDueRecoveryStep()) {
        case "not-started":
          return '';
        case "waiting":
          return 'Validating payment info';
        case "retrying":
          return 'Retrying failed payment';
        case "success":
          return 'Success!'
        case "retry-failed":
          return 'Payment failed.'
      }
    }
  )

  public readonly pastDueDescription = computed(
    () => {
      switch (this.pastDueRecoveryStep()) {
        case "not-started":
          return '';
        case "waiting":
          return 'This only takes a couple of seconds';
        case "retrying":
          return 'This only takes a couple more seconds';
        case "success":
          return 'Your account is fully up-to-date!';
        case "retry-failed":
          if (this.pastDueRetryFailureReason() === 'insufficient-funds') {
            return 'Payment failed due to insufficient funds. Please use a different card or add funds to continue using Homey.';
          }
          return 'We tried charging your new payment method, but the payment didn\'t go through. Please update your payment details to continue using Homey.'
      }
    }
  )

  public async startReturnFromPastDueProcedure() {
    this.pastDueRetryFailureReason.set('none');
    this.pastDueRecoveryStep.set('waiting');
    try {
      await waitAsync(4000);
      await firstValueFrom(this.userService.retrieveUser());

      let user = this.userService.user();

      if (user && isPostSignupFlowUser(user) && user.paymentInfo.type === 'stripe') {
        if (user.paymentInfo.subscriptionStatus === 'past_due') {
          this.pastDueRecoveryStep.set('retrying');

          try {
            await firstValueFrom(this.retryFailedPayment());
            await waitAsync(3000);
            await firstValueFrom(this.userService.retrieveUser());

            user = this.userService.user();
            if (user && isPostSignupFlowUser(user) && user.paymentInfo.type === 'stripe' && user.paymentInfo.subscriptionStatus !== 'past_due') {
              this.pastDueRecoveryStep.set('success');
            } else {
              this.pastDueRecoveryStep.set('retry-failed')
            }
          } catch (e: unknown) {
            this.pastDueRecoveryStep.set('retry-failed');
            if (this.isInsufficientFundsError(e)) {
              this.pastDueRetryFailureReason.set('insufficient-funds');
            }
            console.error('Error during retrying failed payment:', e);
          }
      } else if (user.paymentInfo.subscriptionStatus === 'active') {
        this.pastDueRecoveryStep.set('success');
        }
      }
    } catch (e: unknown) {
      this.pastDueRecoveryStep.set('retry-failed');
      if (this.isInsufficientFundsError(e)) {
        this.pastDueRetryFailureReason.set('insufficient-funds');
      }
      console.error('Error during past due recovery:', e);
    }
  }

  private isInsufficientFundsError(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 400 && error.error?.message === 'insufficient-funds';
    }

    return false;
  }

  public resetPastDueDialog() {
    this.pastDueRetryFailureReason.set('none');
    this.pastDueRecoveryStep.set('not-started');
  }

  private isDeferredNoTrial(): boolean {
    const user = this.userService.user();
    return !!user
      && user.status === 'mandatory-flow'
      && user.paymentInfo.type === 'deferred'
      && !user.paymentInfo.includeTrial;
  }
}
